import { lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "@mui/material";

import { theme } from "@/context/ThemeContext";
import OnboardingContextProvider from "@/context/OnboardingContext";
import AIConfigProvider from "@/context/AIConfigProvider";
import TemplateProvider from "@/context/TemplateProvider";
import ImageLibraryProvider from "@/context/ImageLibraryProvider";
import useFavicon from "./hooks/useFavicon";
import ErrorBoundary from "./modules/app/ErrorBoundary";
import SnackbarNotification from "./modules/app/SnackbarNotification";
import AppProvider from "./context/AppContext";
import GlobalErrorPage from './modules/app/GlobalErrorPage'
import { ModalProvider } from './context/ModalContext'
import SharedTemplateProvider from './context/SharedTemplateProvider'

const queryClient = new QueryClient();

const OnboardingPage = lazy(() =>
  import("./pages/OnboardingPage").then((module) => ({
    default: module.default,
  }))
);
const GettingStartedPage = lazy(() => import("./pages/GettingStartedPage"));
const OnboardingFormPage = lazy(() => import("./pages/OnboardingFormPage"));

const AllTemplatesPage = lazy(() => import("./pages/AllTemplatesPage"));

const init = async () => {
  if(import.meta.env.VITE_USE_MOCK == "true") {
    const { worker } = await import('./mocks/browser')
    worker.start()
  }
}

const App = () => {
  init()
  useFavicon();
  
  return (
    <>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={theme}>
          <ErrorBoundary fallback={GlobalErrorPage}>
            <AppProvider>
              <TemplateProvider>
                <Router basename="">
                  <ModalProvider>
                    <OnboardingContextProvider>
                      <AIConfigProvider>
                        <SharedTemplateProvider>
                          <ImageLibraryProvider>
                            <Suspense fallback={<div>Loading...</div>}>
                            <Routes>
                              {/* <Route path="/" element={<HomePage />} /> */}
                              <Route path="/" element={<GettingStartedPage />} />
                              <Route
                                path="/onboarding"
                                element={
                                  <OnboardingPage defaultTemplate="ecommerce" />
                                }
                              />
                              <Route path="/form/*" element={<OnboardingFormPage />} />
                              <Route
                                path="/all_templates"
                                element={<AllTemplatesPage />}
                              />
                              <Route path="*" element={<Navigate to="/" replace />} />
                            </Routes>
                            </Suspense>
                          </ImageLibraryProvider>
                        </SharedTemplateProvider>
                      </AIConfigProvider>
                    </OnboardingContextProvider>
                  </ModalProvider>
                </Router>
                <SnackbarNotification />
                <div id="image-library-mfe" />
              </TemplateProvider>
            </AppProvider>
          </ErrorBoundary>
        </ThemeProvider>
      </QueryClientProvider>
    </>
  );
};

export default App;
